import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { BLOG } from 'src/app/data/blog/blog.data';
import { BLOG_CATEGORIES } from 'src/app/data/blog/blog-category.data';
import { URLS } from 'src/app/data/navigation/navigation.data';
import { Blog } from 'src/app/model/blog/blog.model';

interface CategorySection {
  key: string;
  label: string;
  eyebrow: string;
  lead: string;
  posts: Blog[];
  /** every post's reading time together */
  minutes: number;
}

// each category's subject in a word, over its name (owner 2026-10-12)
const CATEGORY_EYEBROWS: { [ key: string ]: string } = {
  'color-theory': 'Basics',
  'palette-harmony': 'Pairing',
  'accessibility': 'Contrast',
  'places': 'Places',
  'platforms': 'Editors',
  'open-source': 'Code',
};

// each category's line under its name (no dashes, no closing period: standing copy rules)
const CATEGORY_LEADS: { [ key: string ]: string } = {
  'color-theory': 'How color works: hue, contrast and perception',
  'palette-harmony': 'Building balanced palettes, complementary, analogous and triadic',
  'accessibility': 'Readable color: contrast, WCAG and APCA, and color vision deficiency',
  'places': 'The cities and landscapes behind Codigrate themes',
  'platforms': 'JetBrains, VS Code, Cursor, Chrome, Slack, Ghostty and beyond',
  'open-source': 'Our open source theme repositories and how they are built',
};

/**
 * The blog (owner 2026-10-12, laid out as the Apps & Plugins page): the hero band with the feeds, then a box per
 * category as the home's wide boxes, the category at the left and its posts as small cards (three to a row) at the
 * right, newest first. /blog?category=<key> shows that category.
 */
@Component( {
  selector: 'app-blog-list-page',
  templateUrl: './blog-list-page.component.html',
  styleUrls: [
    '../../tool-pages/tools-page/tools-page.component.scss',
    '../../plugin-pages/plugins-page/plugins-page.component.scss',
    './blog-list-page.component.scss',
  ],
} )
export class BlogListPageComponent implements OnInit, AfterViewInit, OnDestroy {

  URLS = URLS;

  sections: CategorySection[] = [];
  /** the chosen category; null = every post */
  category: string | null = null;

  private all: Blog[] = [];
  private sub?: Subscription;

  constructor( private route: ActivatedRoute ) {
  }

  /** the category boxes shown: every category, or the one /blog?category= names */
  get shownSections(): CategorySection[] {
    return this.category ? this.sections.filter( s => s.key === this.category ) : this.sections;
  }

  ngOnInit(): void {
    this.all = [ ...BLOG ].sort( ( a, b ) => ( a.date < b.date ? 1 : a.date > b.date ? -1 : 0 ) );
    this.sections = BLOG_CATEGORIES
      .filter( c => this.all.some( p => p.category === c.key ) )
      .map( c => ( {
        key: c.key,
        label: c.label,
        eyebrow: CATEGORY_EYEBROWS[ c.key ] || c.label,
        lead: CATEGORY_LEADS[ c.key ] || c.description.replace( /\.$/, '' ),
        posts: this.all.filter( p => p.category === c.key ),
        minutes: this.all.filter( p => p.category === c.key ).reduce( ( n, p ) => n + ( p.readingTimeMinutes || 0 ), 0 ),
      } ) );
  }

  ngAfterViewInit(): void {
    // /blog?category=<key> opens filtered
    this.sub = this.route.queryParamMap.subscribe( params => {
      const cat = params.get( 'category' );
      if ( cat && this.sections.some( s => s.key === cat ) ) {
        setTimeout( () => this.apply( cat ) );
      }
    } );
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /** a category chip: scroll to its box (the whole list stays) and put the keyboard focus there */
  jumpTo( key: string, event?: Event ): void {
    event?.preventDefault();
    const box = document.getElementById( 'cat-' + key );
    if ( !box ) { return; }
    box.focus( { preventScroll: true } );
    // the app scrolls on <body>, where a smooth scrollIntoView does nothing: tween its scrollTop, clearing the nav
    const scroller = document.body.scrollHeight > document.body.clientHeight + 4 ? document.body : document.documentElement;
    const offset = window.matchMedia( '(max-width: 1023px)' ).matches ? 70 : 90;
    const from = scroller.scrollTop;
    const to = Math.max( 0, box.getBoundingClientRect().top + from - offset );
    const start = performance.now();
    const step = ( now: number ) => {
      const t = Math.min( 1, ( now - start ) / 420 );
      scroller.scrollTop = from + ( to - from ) * ( 1 - Math.pow( 1 - t, 3 ) );
      if ( t < 1 ) { requestAnimationFrame( step ); }
    };
    requestAnimationFrame( step );
  }

  trackPost( index: number, post: Blog ): string {
    return post.key;
  }

  private apply( key: string | null ): void {
    this.category = key;
  }
}
